import React from 'react';
import dayjs from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import weekday from 'dayjs/plugin/weekday';
import { cn } from '../../lib/utils';
import { ChevronRightIcon } from '../../assets/ChevronRightIcon';
import 'dayjs/locale/en';
import 'dayjs/locale/pl';
import 'dayjs/locale/ro';
import 'dayjs/locale/ru';
import { CalendarProps, CalendarView } from './DatePicker.interface';
dayjs.extend(localeData);
dayjs.extend(weekday);

export function Calendar({
	initialDate,
	onDateChange,
	minDate,
	maxDate,
	allowOtherMonthSelection = false,
	locale = 'en',
}: CalendarProps) {
	React.useEffect(() => {
		dayjs.locale(locale);
	}, [locale]);

	const defaultDate = initialDate != null ? initialDate : new Date();
	const [currentDate, setCurrentDate] = React.useState(dayjs(defaultDate));
	const [selectedDate, setSelectedDate] = React.useState(dayjs(defaultDate));
	const [currentView, setCurrentView] = React.useState<CalendarView>('days');
	const [previousView, setPreviousView] = React.useState<CalendarView>('days');

	const months = React.useMemo(() => {
		return Array.from({ length: 12 }).map((_, i) => {
			const month = dayjs().locale(locale).month(i).format('MMMM');
			return month.charAt(0).toUpperCase() + month.slice(1);
		});
	}, [locale]);

	const weekdayNames = React.useMemo(() => {
		return dayjs().locale(locale).localeData().weekdaysMin();
	}, [locale]);

	const reorderedWeekdays = React.useMemo(() => {
		const days = [...weekdayNames];
		const sunday = days.shift();
		if (sunday) days.push(sunday);
		return days;
	}, [weekdayNames]);

	const getDaysInMonth = (date: dayjs.Dayjs) => {
		return date.daysInMonth();
	};

	const getFirstDayOfMonth = (date: dayjs.Dayjs) => {
		const day = date.startOf('month').day();
		return day === 0 ? 7 : day;
	};

	const isDateInRange = (date: dayjs.Dayjs) => {
		if (
			minDate &&
			date.startOf('day').isBefore(dayjs(minDate).startOf('day'))
		) {
			return false;
		}
		if (maxDate && date.startOf('day').isAfter(dayjs(maxDate).startOf('day'))) {
			return false;
		}
		return true;
	};

	const handlePrevMonth = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();

		if (currentView === 'days') {
			setCurrentDate((prev) => prev.subtract(1, 'month'));
		} else if (currentView === 'months') {
			setCurrentDate((prev) => prev.subtract(1, 'year'));
		} else if (currentView === 'years') {
			setCurrentDate((prev) => prev.subtract(10, 'year'));
		}
	};

	const handleNextMonth = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();

		if (currentView === 'days') {
			setCurrentDate((prev) => prev.add(1, 'month'));
		} else if (currentView === 'months') {
			setCurrentDate((prev) => prev.add(1, 'year'));
		} else if (currentView === 'years') {
			setCurrentDate((prev) => prev.add(10, 'year'));
		}
	};

	const handleMonthClick = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setCurrentView(currentView === 'months' ? 'days' : 'months');
	};

	const handleYearClick = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setCurrentView(
			currentView === 'years'
				? previousView === 'months'
					? 'months'
					: 'days'
				: 'years',
		);
		if (currentView !== 'years') {
			setPreviousView(currentView);
		}
	};

	const handleMonthSelect = (monthIndex: number, e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setCurrentDate((prev) => prev.month(monthIndex));
		setCurrentView('days');
	};

	const handleYearSelect = (year: number, e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setCurrentDate((prev) => prev.year(year));
		setCurrentView('months');
	};

	const handleDateClick = (
		day: number,
		month: number,
		year: number,
		e: React.MouseEvent,
	) => {
		e.preventDefault();
		e.stopPropagation();

		const newDate = dayjs().year(year).month(month).date(day);

		if (!isDateInRange(newDate)) {
			return;
		}

		setSelectedDate(newDate);
		onDateChange?.(newDate.toDate());
	};

	const renderCalendarDays = () => {
		const year = currentDate.year();
		const month = currentDate.month();

		const daysInMonth = getDaysInMonth(currentDate);
		const firstDayOfMonth = getFirstDayOfMonth(currentDate);

		const prevMonth = month === 0 ? 11 : month - 1;
		const prevMonthYear = month === 0 ? year - 1 : year;
		const prevMonthDate = dayjs().year(prevMonthYear).month(prevMonth);
		const daysInPrevMonth = getDaysInMonth(prevMonthDate);

		const nextMonth = month === 11 ? 0 : month + 1;
		const nextMonthYear = month === 11 ? year + 1 : year;

		const days = [];

		// Previous month days
		for (let i = firstDayOfMonth - 2; i >= 0; i--) {
			const day = daysInPrevMonth - i;
			const date = dayjs().year(prevMonthYear).month(prevMonth).date(day);
			const isInRange = isDateInRange(date);
			const isSelectable = allowOtherMonthSelection && isInRange;
			const isSelected =
				selectedDate.date() === day &&
				selectedDate.month() === prevMonth &&
				selectedDate.year() === prevMonthYear;

			const showGradient = isSelected && isInRange && allowOtherMonthSelection;

			days.push(
				<div
					key={`prev-${day}`}
					onClick={(e) =>
						isSelectable && handleDateClick(day, prevMonth, prevMonthYear, e)
					}
					className={cn(
						'text-center w-[32px] h-[32px] flex items-center justify-center rounded-lg',
						showGradient ? 'bg-gradient-primary text-white cursor-pointer' : '',
						isSelectable && !showGradient
							? 'cursor-pointer text-gray-80 text-sm hover:bg-gray-10'
							: !showGradient
							? 'text-gray-40 text-xs'
							: '',
					)}
				>
					{day.toString().padStart(2, '0')}
				</div>,
			);
		}

		// Current month days
		for (let day = 1; day <= daysInMonth; day++) {
			const date = dayjs().year(year).month(month).date(day);
			const isSelected =
				selectedDate.date() === day &&
				selectedDate.month() === month &&
				selectedDate.year() === year;

			const isWeekend = date.day() === 0 || date.day() === 6;
			const isInRange = isDateInRange(date);
			const isSelectable = isInRange;

			const showGradient = isSelected && isInRange;

			days.push(
				<div
					key={`current-${day}`}
					onClick={(e) => isSelectable && handleDateClick(day, month, year, e)}
					className={cn(
						'text-center w-[32px] h-[32px] flex items-center justify-center rounded-lg transition',
						showGradient ? 'bg-gradient-primary text-white cursor-pointer' : '',
						isSelectable && !showGradient
							? 'cursor-pointer text-sm hover:bg-gray-10'
							: !showGradient
							? 'text-gray-40 text-xs'
							: '',
						!isSelected && !showGradient && isWeekend ? 'text-gray-400' : '',
						!isInRange ? 'text-gray-40 text-xs cursor-not-allowed' : '',
					)}
				>
					{day.toString().padStart(2, '0')}
				</div>,
			);
		}

		// Next month days
		const totalCells = 42; // 6 rows x 7 days
		const nextMonthDays = totalCells - days.length;

		for (let day = 1; day <= nextMonthDays; day++) {
			const date = dayjs().year(nextMonthYear).month(nextMonth).date(day);
			const isInRange = isDateInRange(date);
			const isSelectable = allowOtherMonthSelection && isInRange;
			const isSelected =
				selectedDate.date() === day &&
				selectedDate.month() === nextMonth &&
				selectedDate.year() === nextMonthYear;

			const showGradient = isSelected && isInRange && allowOtherMonthSelection;

			days.push(
				<div
					key={`next-${day}`}
					onClick={(e) =>
						isSelectable && handleDateClick(day, nextMonth, nextMonthYear, e)
					}
					className={cn(
						'text-center w-[32px] h-[32px] flex items-center justify-center rounded-lg transition',
						showGradient ? 'bg-gradient-primary text-white cursor-pointer' : '',
						isSelectable && !showGradient
							? 'cursor-pointer text-gray-80 text-sm hover:bg-gray-10'
							: !showGradient
							? 'text-gray-40 text-xs'
							: '',
					)}
				>
					{day.toString().padStart(2, '0')}
				</div>,
			);
		}

		return days;
	};

	const renderMonths = () => {
		return (
			<div className="grid grid-cols-3 gap-3 p-2">
				{months.map((month, index) => {
					const isSelected = currentDate.month() === index;

					// Check if month is in range
					const firstOfMonth = dayjs()
						.year(currentDate.year())
						.month(index)
						.startOf('month');
					const lastOfMonth = dayjs()
						.year(currentDate.year())
						.month(index)
						.endOf('month');
					const isInRange =
						(!minDate || lastOfMonth.isAfter(dayjs(minDate))) &&
						(!maxDate ||
							firstOfMonth.isBefore(dayjs(maxDate).endOf('day')) ||
							firstOfMonth.isSame(dayjs(maxDate), 'day'));

					const showGradient = isSelected && isInRange;

					return (
						<div
							key={month}
							onClick={(e) => isInRange && handleMonthSelect(index, e)}
							className={cn(
								'text-center px-1 w-full h-[32px] flex items-center justify-center rounded-lg text-sm transition',
								showGradient
									? 'bg-gradient-primary text-white cursor-pointer'
									: '',
								isInRange && !showGradient
									? 'cursor-pointer hover:bg-gray-10'
									: !showGradient
									? 'text-gray-40 cursor-not-allowed'
									: '',
							)}
						>
							{month.substring(0, 3)}
						</div>
					);
				})}
			</div>
		);
	};

	const renderYears = () => {
		const currentYear = currentDate.year();
		const startYear = Math.floor(currentYear / 10) * 10 - 1;
		const years = Array.from({ length: 12 }, (_, i) => startYear + i);

		return (
			<div className="grid grid-cols-3 gap-2 p-2">
				{years.map((year) => {
					const isSelected = currentDate.year() === year;

					// Check if year is in range
					const firstOfYear = dayjs().year(year).startOf('year');
					const lastOfYear = dayjs().year(year).endOf('year');
					const isInRange =
						(!minDate || lastOfYear.isAfter(dayjs(minDate))) &&
						(!maxDate ||
							firstOfYear.isBefore(dayjs(maxDate).endOf('day')) ||
							firstOfYear.isSame(dayjs(maxDate), 'year'));

					const showGradient = isSelected && isInRange;

					return (
						<div
							key={year}
							onClick={(e) => isInRange && handleYearSelect(year, e)}
							className={cn(
								'text-center w-full h-[32px] flex items-center justify-center rounded-lg text-sm transition',
								showGradient
									? 'bg-gradient-primary text-white cursor-pointer'
									: '',
								year === startYear || year === startYear + 11
									? 'text-gray-400'
									: '',
								isInRange && !showGradient
									? 'cursor-pointer hover:bg-gray-10'
									: !showGradient
									? 'text-gray-40 cursor-not-allowed'
									: '',
							)}
						>
							{year}
						</div>
					);
				})}
			</div>
		);
	};

	const renderHeader = () => {
		return (
			<div className="w-full flex items-center justify-between mb-4">
				<button
					onClick={handlePrevMonth}
					className="w-[20px] h-[20px] flex items-center justify-center p-0 m-0 rounded-md hover:bg-gray-100 border border-solid border-gray-40 bg-transparent cursor-pointer transition hover:border-gray-80"
					aria-label={`Previous ${
						currentView === 'days'
							? 'month'
							: currentView === 'months'
							? 'year'
							: 'decade'
					}`}
					type="button"
				>
					<span className="flex rotate-180">
						<ChevronRightIcon />
					</span>
				</button>

				<button
					onClick={handleMonthClick}
					className={cn(
						'h-[22px] flex justify-center items-center gap-[10px] px-3 border-[1px] border-transparent border-solid bg-transparent cursor-pointer rounded-md',
						currentView === 'months'
							? 'bg-gray-100'
							: 'transition hover:border-gray-40',
					)}
					type="button"
				>
					{months[currentDate.month()]}
					<span className="flex rotate-90">
						<ChevronRightIcon />
					</span>
				</button>

				<button
					onClick={handleYearClick}
					className={cn(
						'h-[22px] flex justify-center items-center gap-[10px] px-3 border-[1px] border-transparent border-solid bg-transparent cursor-pointer rounded-md',
						currentView === 'years'
							? 'bg-gray-100'
							: 'transition hover:border-gray-40',
					)}
					type="button"
				>
					{currentDate.year()}
					<span className="flex rotate-90">
						<ChevronRightIcon />
					</span>
				</button>

				<button
					onClick={handleNextMonth}
					className="w-[20px] h-[20px] flex items-center justify-center p-0 m-0 rounded-md hover:bg-gray-100 border border-solid border-gray-40 bg-transparent cursor-pointer transition hover:border-gray-80"
					aria-label={`Next ${
						currentView === 'days'
							? 'month'
							: currentView === 'months'
							? 'year'
							: 'decade'
					}`}
					type="button"
				>
					<span className="flex">
						<ChevronRightIcon />
					</span>
				</button>
			</div>
		);
	};

	const renderCalendarContent = () => {
		switch (currentView) {
			case 'months':
				return renderMonths();
			case 'years':
				return renderYears();
			case 'days':
			default:
				return (
					<>
						<div className="grid grid-cols-7 gap-1 mb-2">
							{reorderedWeekdays.map((day) => (
								<div
									key={day}
									className="text-center text-xs font-medium text-gray-40 w-8 h-8 flex items-center justify-center"
								>
									{day.charAt(0)}
								</div>
							))}
						</div>
						<div className="grid grid-cols-7 gap-1">{renderCalendarDays()}</div>
					</>
				);
		}
	};

	return (
		<div
			className="bg-white rounded-lg shadow-md p-4 w-[280px] box-border"
			onClick={(e) => e.stopPropagation()}
		>
			{renderHeader()}
			<div className="border-0 border-solid border-t-[1px] border-gray-10" />
			{renderCalendarContent()}
		</div>
	);
}
